import { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import { useFeatureFlagVariantKey } from "posthog-js/react";

import { useUser } from "context/UserContext";
import useQueryParams from "hooks/useQueryParams";
import { fetchPublicAPI, useFetchPublicAPI } from "hooks/usePublicAPI";
import { OnboardingContainer } from "layouts/OnboardingLayout";
import Header from "components/Header";
import CalEmbed from "components/CalEmbed";
import PaymentMethodForm, {
  PaymentMethodFormData,
} from "components/PaymentMethodForm";
import { PRE_NEED_LEAD_SCHEDULE_CONFIG } from "constants/onboarding-pre-need";

function FormSkelly() {
  return (
    <div className="flex items-center justify-center">
      <div className="w-full animate-pulse space-y-6">
        <div className="h-10 w-1/3 rounded bg-gray-200"></div>
        <div className="space-y-4">
          <div className="h-14 w-full rounded bg-gray-200"></div>
          <div className="flex space-x-2">
            <div className="h-14 w-full rounded bg-gray-200"></div>
            <div className="h-14 w-full rounded bg-gray-200"></div>
          </div>
          <div className="h-14 w-full rounded bg-gray-200"></div>
        </div>
        <div className="h-10 w-1/4 rounded bg-gray-200"></div>
      </div>
    </div>
  );
}

export default function QuotePayment() {
  const [searchParamsObject] = useQueryParams();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { posthogFlagsReady } = useUser();

  // The test arm reaches this page via the "start your application" path, so
  // they may not have booked a call yet — surface the booking CTA for them;
  // control already has an appointment. The person signal id set earlier in the
  // funnel stays as PostHog's anonymous distinct id (shared cross-subdomain), so
  // we only trust the variant once flags are ready; before that it reads as
  // undefined.
  const scheduleGateVariant = useFeatureFlagVariantKey(
    "pre-need-schedule-gate-app-path",
  );
  const showBookingCta = posthogFlagsReady && scheduleGateVariant === "test";
  const { data, error, isLoading } = useFetchPublicAPI<{
    status: string;
    event_id?: string;
    ped?: string;
  }>({
    url: `/pre-need/onboarding/policies/${searchParamsObject.p}/status`,
    options: {
      method: "GET",
    },
  });

  const handleSubmit = useCallback(
    async (formData: PaymentMethodFormData) => {
      const response = await fetchPublicAPI(
        `/pre-need/onboarding/policies/${searchParamsObject.p}/actions/add-payment-method`,
        {
          method: "POST",
          body: JSON.stringify({
            payload: formData,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status !== "ok") {
        // TODO: Make this error message more useful
        throw new Error("Error adding payment method");
      }

      setIsSubmitted(true);
    },
    [searchParamsObject],
  );

  if (
    !searchParamsObject.p ||
    (!isLoading && data?.status !== "onboarding") ||
    !!error
  ) {
    console.log("Policy not in onboarding status", data, window.location.href);
    // If their signatures are already pending, send them to the finalize page
    if (data?.status === "pending-signatures") {
      console.log(
        "Policy onboarding status indicates signatures are pending, sending to finalize page",
      );
      return (
        <Navigate
          to={`/continue/pre-planning/finalize?p=${searchParamsObject.p}`}
        />
      );
    }

    return (
      <OnboardingContainer>
        <Header
          properties={{ call_or_schedule: PRE_NEED_LEAD_SCHEDULE_CONFIG }}
        />

        <div className="mx-auto my-16 max-w-xl px-3 sm:px-4">
          <h1 className="mb-4 text-center">Something isn’t quite right</h1>

          <div className="space-y-2">
            <p>
              It looks like we’re missing some important information to guide
              you to the right place.
            </p>
            <p>Please try clicking the link exactly as it was sent to you.</p>
            <p>
              If the problem persists, please reach out to us at{" "}
              <a
                href="mailto:preplan@after.com"
                className="underline-offset-3 underline decoration-dotted"
              >
                preplan@after.com
              </a>
              .
            </p>
          </div>
        </div>
      </OnboardingContainer>
    );
  }

  return (
    <OnboardingContainer>
      <div className="w-full px-3 sm:px-4">
        <Header
          properties={{ call_or_schedule: PRE_NEED_LEAD_SCHEDULE_CONFIG }}
        />

        <div className="mx-auto my-16 max-w-xl">
          {isLoading ? (
            <FormSkelly />
          ) : isSubmitted ? (
            <div className="text-center">
              <h1 className="mb-4 text-2xl font-semibold">
                You&rsquo;re 15 minutes from total Peace of Mind
              </h1>
              <p>
                Your application is in. One short conversation with a US-based
                pre-planning representative to go over your quote is all
                that&rsquo;s left to finalize your policy.
              </p>
              {showBookingCta && (
                <div className="mt-8">
                  <p className="mb-4 text-sm text-gray-600">
                    Don&rsquo;t want to wait for us to reach out? Pick a time
                    below, or{" "}
                    <a
                      href={`tel:${PRE_NEED_LEAD_SCHEDULE_CONFIG.phone.number}`}
                      className="underline decoration-dotted"
                    >
                      call us now
                    </a>
                    .
                  </p>
                  <CalEmbed
                    isInline
                    afterId={PRE_NEED_LEAD_SCHEDULE_CONFIG.calendly.after_id}
                    eventUrl={PRE_NEED_LEAD_SCHEDULE_CONFIG.calendly.event_url}
                    analytics={PRE_NEED_LEAD_SCHEDULE_CONFIG.calendly.analytics}
                    prefillData={
                      data?.event_id ? { event_id: data.event_id } : undefined
                    }
                    dataPayload={data?.ped}
                    buttonContent={null}
                  />
                </div>
              )}
            </div>
          ) : (
            <PaymentMethodForm
              onSubmit={handleSubmit}
              eventId="pre-need-policy-onboarding"
              defaultMethod="bank"
            />
          )}
        </div>
      </div>
    </OnboardingContainer>
  );
}
